import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchObjectTypes,
  previewDataManagement,
  exportDataManagement,
  deleteDataManagement,
  moveDataManagement,
  fetchAuditLog,
} from '../shared/api/dataManagement';
import SearchableSelect from "../components/SearchableSelect";
import { TableActionColumnHeader } from "../components/TableShell";
import { getAuthHeaders } from '../shared/api/authHeaders';
const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;

const STEPS = ['Select Tenant', 'Select Objects', 'Choose Action', 'Options & Preview', 'Confirm', 'Result'];
const ACTIONS = [
  { id: 'export', label: 'Export JSON' },
  { id: 'delete', label: 'Delete Data' },
  { id: 'move', label: 'Move to Another Tenant' },
];

/** Related record labels per object type (must match server relationship config). Used to show what "Include related data" will include. */
const RELATED_BY_OBJECT_TYPE = {
  Users: ['Event Participants', 'Program Leads', 'User Table Preferences', 'Google Calendar Connections', 'Saved Insights', 'Participant Contact Logs'],
  Vendors: ['Tasks', 'Sub Tasks', 'Time Blocks'],
  Projects: [],
  Programs: ['Program Leads', 'Events', 'Event Participants'],
  Events: ['Event Participants'],
  EventParticipants: [],
  Surveys: ['Survey Questions', 'Survey Invitations', 'Survey Responses'],
  Tasks: ['Sub Tasks', 'Time Blocks'],
  Doctors: [],
  Pharmacies: [],
  Medications: [],
  IntakeLogs: [],
  Solutions: ['Solution Workflows'],
  SOPs: [],
};

function DataManagement() {
  const { userInfo } = useAuth();
  const role = userInfo?.role;
  const performedBy = userInfo?._id || '';
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [accounts, setAccounts] = useState([]);
  const [objectTypes, setObjectTypes] = useState([]);
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [sourceAccountId, setSourceAccountId] = useState('');
  const [targetAccountId, setTargetAccountId] = useState('');
  const [selectedObjectTypes, setSelectedObjectTypes] = useState([]);
  const [action, setAction] = useState('export');
  const [moveMode, setMoveMode] = useState('copy'); // 'copy' | 'move' when action === 'move'
  const [includeRelated, setIncludeRelated] = useState(true);
  const [previewCounts, setPreviewCounts] = useState(null);
  const [result, setResult] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogEntries, setAuditLogEntries] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  useEffect(() => {
    if (role !== 'Super Admin') return;
    let cancelled = false;
    getAuthHeaders()
      .then((headers) =>
        fetch(serverUrl + '/getAccount', {
          method: 'POST',
          headers,
          body: JSON.stringify({ account_id: userInfo?.account_id }),
        })
      )
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setAccounts(data);
      })
      .catch((err) => !cancelled && setError(getMessageForFetchError(err)));
    return () => { cancelled = true; };
  }, [role, serverUrl, userInfo?.account_id]);

  useEffect(() => {
    let cancelled = false;
    fetchObjectTypes()
      .then((data) => {
        if (!cancelled && data.objectTypes) setObjectTypes(data.objectTypes);
      })
      .catch((err) => !cancelled && setError(err.message || 'Failed to load object types'));
    return () => { cancelled = true; };
  }, []);

  const targetAccount = accounts.find((a) => a._id === targetAccountId);

  /** Related record type labels for the currently selected object types (deduplicated). */
  const relatedRecordLabels = React.useMemo(() => {
    const set = new Set();
    selectedObjectTypes.forEach((typeId) => {
      (RELATED_BY_OBJECT_TYPE[typeId] || []).forEach((label) => set.add(label));
    });
    return Array.from(set).sort();
  }, [selectedObjectTypes]);

  const loadAuditLog = () => {
    if (!showAuditLog) {
      setShowAuditLog(true);
      setAuditLogLoading(true);
      fetchAuditLog({ role, limit: 100 })
        .then((data) => setAuditLogEntries(data.entries || []))
        .catch((err) => setError(err.message || 'Failed to load audit log'))
        .finally(() => setAuditLogLoading(false));
    } else {
      setShowAuditLog(false);
    }
  };

  const runPreview = () => {
    if (!sourceAccountId || selectedObjectTypes.length === 0) {
      setError('Select a tenant and at least one object type.');
      return;
    }
    setError('');
    setLoading(true);
    previewDataManagement({
      role,
      account_id: sourceAccountId,
      objectTypes: selectedObjectTypes,
      includeRelated,
      performed_by: performedBy,
    })
      .then((data) => {
        setPreviewCounts(data.counts || {});
        setStep(4);
      })
      .catch((err) => setError(err.message || 'Preview failed'))
      .finally(() => setLoading(false));
  };

  const executeAction = () => {
    setError('');
    setLoading(true);
    const baseBody = {
      role,
      objectTypes: selectedObjectTypes,
      includeRelated,
      performed_by: performedBy,
    };
    if (action === 'export') {
      exportDataManagement({ ...baseBody, account_id: sourceAccountId })
        .then((data) => {
          setResult({ action: 'export', counts: data.counts, payload: data.payload });
          setStep(6);
        })
        .catch((err) => setError(err.message || 'Export failed'))
        .finally(() => setLoading(false));
    } else if (action === 'delete') {
      deleteDataManagement({ ...baseBody, account_id: sourceAccountId })
        .then((data) => {
          setResult({ action: 'delete', counts: data.counts });
          setStep(6);
        })
        .catch((err) => setError(err.message || 'Delete failed'))
        .finally(() => setLoading(false));
    } else if (action === 'move') {
      if (!targetAccountId || targetAccountId === sourceAccountId) {
        setError('Select a different target tenant.');
        setLoading(false);
        return;
      }
      moveDataManagement({
        ...baseBody,
        source_account_id: sourceAccountId,
        target_account_id: targetAccountId,
        mode: moveMode,
      })
        .then((data) => {
          setResult({ action: 'move', counts: data.counts, moved: data.moved });
          setStep(6);
        })
        .catch((err) => setError(err.message || 'Move failed'))
        .finally(() => setLoading(false));
    }
  };

  const toggleObjectType = (id) => {
    setSelectedObjectTypes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const totalPreviewRecords = previewCounts
    ? Object.values(previewCounts).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)
    : 0;

  const downloadExport = () => {
    if (!result || result.action !== 'export' || !result.payload) return;
    const filename = `data_export_${new Date().toISOString().slice(0, 10)}.json`;
    const blob = new Blob([JSON.stringify(result.payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (role !== 'Super Admin') {
    return (
      <div className="page-container">
        <h1>Data Management</h1>
        <p>Only Super Admins can access this tool.</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1>Data Management</h1>
      <p className="page-description">
        Manage tenant-specific data for go-live, testing, and environment setup. Select an account, object types, and an action.
      </p>

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div className="data-mgmt-wizard">
        <div className="data-mgmt-steps">
          {STEPS.map((label, i) => (
            <span
              key={label}
              className={`data-mgmt-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}
            >
              {i + 1}. {label}
            </span>
          ))}
        </div>

        {step === 0 && (
          <section className="data-mgmt-section">
            <h2>Select Account (Source Tenant)</h2>
            <label className="data-mgmt-account-label">
              Account
              <div style={{ width: "100%", maxWidth: 520 }}>
                <SearchableSelect
                  value={sourceAccountId}
                  onChange={(v) => setSourceAccountId(String(v || ""))}
                  options={(accounts || []).map((a) => ({
                    value: String(a._id),
                    label: String(a?.full_name || a?._id || "Account"),
                    keywords: [a?._id, a?.full_name, a?.site_name].filter(Boolean).join(" "),
                  }))}
                  placeholder="— Select account —"
                  searchPlaceholder="Search account…"
                  allowEmpty={true}
                  emptyLabel="— Select account —"
                  maxVisibleItems={4}
                  dropdownPortal={true}
                  title="Account"
                />
              </div>
            </label>
            <div className="data-mgmt-section-actions">
              <button type="button" className="btn btn-primary" onClick={() => sourceAccountId && setStep(1)} disabled={!sourceAccountId}>
                Next
              </button>
            </div>
          </section>
        )}

        {step === 1 && (
          <section className="data-mgmt-section">
            <h2>Select Object Types</h2>
            <p>Select one or more data objects to operate on.</p>
            <div className="data-mgmt-listbox" role="listbox" aria-multiselectable="true" aria-label="Object types">
              {objectTypes.map((ot) => (
                <label
                  key={ot.id}
                  className={`data-mgmt-listbox-item ${selectedObjectTypes.includes(ot.id) ? 'selected' : ''}`}
                  role="option"
                  aria-selected={selectedObjectTypes.includes(ot.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedObjectTypes.includes(ot.id)}
                    onChange={() => toggleObjectType(ot.id)}
                    tabIndex={-1}
                  />
                  <span className="data-mgmt-listbox-item-label">{ot.label}</span>
                </label>
              ))}
            </div>
            <div className="data-mgmt-section-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(0)}>Back</button>
              <button type="button" className="btn btn-primary" onClick={() => setStep(2)} disabled={selectedObjectTypes.length === 0}>
                Next
              </button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="data-mgmt-section">
            <h2>Choose Action</h2>
            <div className="data-mgmt-actions">
              {ACTIONS.map((a) => (
                <label key={a.id} className="data-mgmt-radio">
                  <input
                    type="radio"
                    name="action"
                    value={a.id}
                    checked={action === a.id}
                    onChange={() => setAction(a.id)}
                  />
                  {a.label}
                </label>
              ))}
            </div>
            {action === 'move' && (
              <>
                <label className="data-mgmt-target">
                  Target Tenant
                  <div style={{ width: "100%", maxWidth: 520 }}>
                    <SearchableSelect
                      value={targetAccountId}
                      onChange={(v) => setTargetAccountId(String(v || ""))}
                      options={(accounts || []).filter((a) => String(a._id) !== String(sourceAccountId)).map((a) => ({
                        value: String(a._id),
                        label: String(a?.full_name || a?._id || "Account"),
                        keywords: [a?._id, a?.full_name, a?.site_name].filter(Boolean).join(" "),
                      }))}
                      placeholder="— Select target account —"
                      searchPlaceholder="Search account…"
                      allowEmpty={true}
                      emptyLabel="— Select target account —"
                      maxVisibleItems={4}
                      dropdownPortal={true}
                      title="Target tenant"
                    />
                  </div>
                </label>
                <div className="data-mgmt-move-mode">
                  <label className="data-mgmt-radio">
                    <input type="radio" name="moveMode" checked={moveMode === 'copy'} onChange={() => setMoveMode('copy')} />
                    Copy (leave source unchanged)
                  </label>
                  <label className="data-mgmt-radio">
                    <input type="radio" name="moveMode" checked={moveMode === 'move'} onChange={() => setMoveMode('move')} />
                    Move (copy then delete from source)
                  </label>
                </div>
              </>
            )}
            <div className="data-mgmt-section-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
              <button type="button" className="btn btn-primary" onClick={() => setStep(3)}>Next</button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="data-mgmt-section">
            <h2>Options & Preview</h2>
            <div className="data-mgmt-options-block">
              <p className="data-mgmt-selected-label">Selected object types</p>
              <p className="data-mgmt-selected-values">{selectedObjectTypes.map((id) => objectTypes.find((ot) => ot.id === id)?.label || id).join(', ')}</p>
              <div className="data-mgmt-include-related-block">
                <label className="data-mgmt-check data-mgmt-check-include">
                  <input
                    type="checkbox"
                    checked={includeRelated}
                    onChange={(e) => setIncludeRelated(e.target.checked)}
                  />
                  Include related data
                </label>
                {relatedRecordLabels.length > 0 ? (
                  <div className="data-mgmt-related-list">
                    <ul className="data-mgmt-related-ul">
                      {relatedRecordLabels.map((label) => (
                        <li key={label}>{label}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="data-mgmt-related-none">No related records for the selected object types.</p>
                )}
              </div>
            </div>
            <div className="data-mgmt-section-actions">
              <button type="button" className="btn btn-primary" onClick={runPreview} disabled={loading}>
                {loading ? 'Loading…' : 'Preview record counts'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
            </div>
          </section>
        )}

        {step === 4 && (
          <section className="data-mgmt-section">
            <h2>Preview</h2>
            <p>You are about to {action === 'export' ? 'export' : action === 'delete' ? 'delete' : 'move'} the following records:</p>
            <ul className="data-mgmt-counts">
              {previewCounts && Object.entries(previewCounts).map(([label, count]) => (
                <li key={label}>{label}: {count}</li>
              ))}
            </ul>
            <p><strong>Total: {totalPreviewRecords} records</strong></p>
            {action === 'delete' && (
              <p className="data-mgmt-warning">
                This will permanently delete these records and related data within the selected tenant. This cannot be undone.
              </p>
            )}
            {action === 'move' && (
              <p className="data-mgmt-info">
                Data will be {moveMode === 'copy' ? 'copied' : 'moved'} to <strong>{targetAccount?.full_name || targetAccountId}</strong>
                {moveMode === 'move' ? ', then removed from the source tenant.' : '.'}
              </p>
            )}
            <div className="data-mgmt-section-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(3)}>Back</button>
              <button type="button" className="btn btn-primary" onClick={executeAction} disabled={loading}>
                {loading ? 'Running…' : action === 'export' ? 'Export JSON' : action === 'delete' ? 'Confirm & Delete' : 'Confirm & Move'}
              </button>
            </div>
          </section>
        )}

        {step === 6 && result && (
          <section className="data-mgmt-section data-mgmt-result">
            <h2>Result</h2>
            {result.action === 'export' && (
              <>
                <p className="data-mgmt-success">Export completed.</p>
                <ul className="data-mgmt-counts">
                  {result.counts && Object.entries(result.counts).map(([label, count]) => (
                    <li key={label}>{label}: {count}</li>
                  ))}
                </ul>
                <p>File: <code>data_export_{new Date().toISOString().slice(0, 10)}.json</code></p>
                <button type="button" className="btn btn-primary" onClick={downloadExport}>
                  Download JSON
                </button>
              </>
            )}
            {(result.action === 'delete' || result.action === 'move') && (
              <>
                <p className="data-mgmt-success">
                  {result.action === 'delete' ? 'Delete' : result.moved ? 'Move' : 'Copy'} completed.
                </p>
                <ul className="data-mgmt-counts">
                  {result.counts && Object.entries(result.counts).map(([label, count]) => (
                    <li key={label}>{label}: {count}</li>
                  ))}
                </ul>
              </>
            )}
            <div className="data-mgmt-section-actions">
              <button type="button" className="btn btn-primary" onClick={() => { setStep(0); setResult(null); setPreviewCounts(null); }}>
                Start over
              </button>
            </div>
          </section>
        )}
      </div>

      <div className="data-mgmt-audit-section" style={{ marginTop: '2rem' }}>
        <button type="button" className="btn btn-secondary" onClick={loadAuditLog}>
          {showAuditLog ? 'Hide audit log' : 'View audit log'}
        </button>
        {showAuditLog && (
          <div className="data-mgmt-audit-box">
            <h2 className="data-mgmt-audit-title">Data Management audit log</h2>
            {auditLogLoading ? (
              <p>Loading…</p>
            ) : (
              <div className="data-mgmt-audit-table-wrap">
                <table className="data-mgmt-audit-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <TableActionColumnHeader align="left" />
                      <th>Result</th>
                      <th>Account</th>
                      <th>Target (move)</th>
                      <th>Object types</th>
                      <th>Include related</th>
                      <th>Counts</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogEntries.length === 0 ? (
                      <tr><td colSpan={9}>No audit entries yet.</td></tr>
                    ) : (
                      auditLogEntries.map((e) => (
                        <tr key={e._id}>
                          <td>{e.performed_at ? new Date(e.performed_at).toLocaleString() : '—'}</td>
                          <td>{e.action}{e.move_mode ? ` (${e.move_mode})` : ''}</td>
                          <td>{e.result}</td>
                          <td>{e.account_id ? e.account_id.slice(-8) : '—'}</td>
                          <td>{e.target_account_id ? e.target_account_id.slice(-8) : '—'}</td>
                          <td>{(e.object_types || []).join(', ')}</td>
                          <td>{e.include_related ? 'Yes' : 'No'}</td>
                          <td>{e.counts && typeof e.counts === 'object' ? Object.entries(e.counts).map(([k, v]) => `${k}: ${v}`).join('; ') : '—'}</td>
                          <td>{e.details || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .data-mgmt-wizard { margin-top: 1.5rem; }
        .data-mgmt-steps { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.5rem; font-size: 0.9rem; color: #666; }
        .data-mgmt-step.active { font-weight: 600; color: #0d6efd; }
        .data-mgmt-step.done { color: #198754; }
        .data-mgmt-section { background: #f8f9fa; padding: 1.25rem; border-radius: 8px; margin-bottom: 1rem; }
        .data-mgmt-section h2 { margin-top: 0; margin-bottom: 1rem; font-size: 1.1rem; }
        .data-mgmt-checkboxes, .data-mgmt-actions { display: flex; flex-wrap: wrap; gap: 1rem; margin-bottom: 1rem; }
        .data-mgmt-check, .data-mgmt-radio { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; }
        .data-mgmt-radio { white-space: nowrap; }
        .data-mgmt-listbox { border: 1px solid #ced4da; border-radius: 6px; background: #fff; min-width: 260px; max-height: 280px; overflow-y: auto; margin-bottom: 1rem; }
        .data-mgmt-listbox-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.75rem; cursor: pointer; border: none; margin: 0; }
        .data-mgmt-listbox-item:hover { background: #f0f7ff; }
        .data-mgmt-listbox-item.selected { background: #e7f1ff; }
        .data-mgmt-listbox-item-label { white-space: nowrap; }
        .data-mgmt-target { display: block; margin-top: 1rem; }
        .data-mgmt-account-label { display: flex; align-items: center; gap: 0.5rem; flex-wrap: nowrap; }
        .data-mgmt-account-label .form-control { flex: 1; min-width: 320px; max-width: 100%; }
        .data-mgmt-target select { margin-left: 0.5rem; min-width: 220px; }
        .data-mgmt-counts { list-style: none; padding-left: 0; }
        .data-mgmt-counts li { padding: 0.25rem 0; }
        .data-mgmt-warning { color: #856404; background: #fff3cd; padding: 0.75rem; border-radius: 6px; }
        .data-mgmt-info { color: #0c5460; background: #d1ecf1; padding: 0.75rem; border-radius: 6px; }
        .data-mgmt-success { color: #198754; font-weight: 600; }
        .data-mgmt-result .btn { margin-right: 0.5rem; margin-top: 0.5rem; }
        .data-mgmt-options-block { margin-bottom: 1.25rem; }
        .data-mgmt-selected-label { margin: 0 0 0.25rem 0; font-size: 0.85rem; color: #666; text-transform: uppercase; letter-spacing: 0.02em; }
        .data-mgmt-selected-values { margin: 0 0 1.25rem 0; font-size: 1rem; }
        .data-mgmt-include-related-block { margin: 0; padding: 0; display: flex; flex-direction: column; }
        .data-mgmt-check-include { display: flex; align-items: center; gap: 0.5rem; margin: 0 0 0.25rem 0; font-weight: 500; white-space: nowrap; }
        .data-mgmt-related-list { display: block; margin: 0.5rem 0 0 1.5rem; padding: 0; }
        .data-mgmt-related-heading { margin: 0 0 0.5rem 0; font-size: 0.9rem; color: #495057; font-weight: 500; }
        .data-mgmt-related-ul { margin: 0; padding-left: 1.25rem; }
        .data-mgmt-related-ul li { padding: 0.15rem 0; line-height: 1.4; }
        .data-mgmt-related-none { margin: 0; font-size: 0.9rem; color: #6c757d; }
        .data-mgmt-section-actions { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid #dee2e6; justify-content: flex-start; width: 100%; }
        .data-mgmt-section-actions .btn { margin: 0; }
        .data-mgmt-audit-box { margin-top: 1rem; background: #f8f9fa; padding: 1rem; border-radius: 8px; border: 1px solid #dee2e6; }
        .data-mgmt-audit-title { margin: 0 0 0.75rem 0; font-size: 1rem; }
        .data-mgmt-audit-table-wrap { overflow-x: auto; }
        .data-mgmt-audit-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        .data-mgmt-audit-table th, .data-mgmt-audit-table td { padding: 0.4rem 0.5rem; text-align: left; border: 1px solid #dee2e6; }
        .data-mgmt-audit-table th { background: #e9ecef; font-weight: 600; }
        .data-mgmt-audit-table tbody tr:nth-child(even) { background: #f8f9fa; }
      `}</style>
    </div>
  );
}

export default DataManagement;
